package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.PeopleViewModel
import com.arena_web.core.viewmodel.ProfileViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.PersonDto
import com.arena_web.data.dto.UserDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.overlay.CompleteProfilePromptDialog
import com.arena_web.ui.components.overlay.MatchPopup
import com.arena_web.ui.components.overlay.PersonDetailDialog
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.people.COMMIT_LIKE
import com.arena_web.ui.sections.people.COMMIT_LIKE_BUTTON
import com.arena_web.ui.sections.people.COMMIT_SKIP
import com.arena_web.ui.sections.people.COMMIT_SKIP_BUTTON
import com.arena_web.ui.sections.people.PeopleCardStack
import com.arena_web.ui.sections.people.mobile.MobilePeopleContent
import com.arena_web.ui.sections.profile.edit.CompleteProfileWizardDialog
import com.arena_web.ui.sections.profile.edit.FocusActivitiesFormState
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.sections.profile.edit.PersonalInfoFormState
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Page("/people")
@Composable
fun PeoplePage() {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val ctx = rememberPageContext()

    val sportName = remember {
        Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
    }

    val userRepo = container.userRepository
    val peopleRepo = container.peopleRepository
    val isGuest = peopleRepo == null

    // ProfileViewModel is the existing source of truth for "who is the
    // current user + what's in their gallery" — reuse it for the
    // completeness check so we don't refetch the same data.
    val profileViewModel = if (userRepo != null) {
        rememberViewModel {
            ProfileViewModel(userRepo, container.teamRepository, container.followRepository, container.cardRepository)
        }
    } else null

    var showAuthGate by remember { mutableStateOf(false) }

    val mobile = isMobile()

    AppPageLayout(
        activeTab = SidebarTab.People,
        title = t.pagePeopleSeoTitle,
        description = t.pagePeopleSeoDescription,
        mobileReady = true,
        mobilePageTitle = t.pagePeopleTitle,
        mobileHeaderAction = com.arena_web.ui.sections.header.MobileHeaderAction.Likes,
    ) {
        val activeRepo = peopleRepo ?: container.publicPeopleRepository

        // Render the Squad Up panel immediately so the header/chrome is
        // visible while the profile fetch is still in flight. The inner
        // content area shows a spinner via PeopleUiState.isLoading until
        // the people feed arrives. Profile-completeness gating inside
        // PeopleContent tolerates a null user (treats it as "not ready",
        // which is harmless because no cards exist to act on yet).
        val peopleViewModel = rememberViewModel(key = if (isGuest) "people-guest" else "people-auth") {
            PeopleViewModel(activeRepo, isGuest = isGuest)
        }

        if (isGuest) {
            PeopleGuestContent(
                viewModel = peopleViewModel,
                sportName = sportName,
                mobile = mobile,
                onGated = { showAuthGate = true },
            )

            com.arena_web.ui.components.overlay.AccountRequiredModal(
                isOpen = showAuthGate,
                onClose = { showAuthGate = false },
                onSignedIn = {
                    showAuthGate = false
                    kotlinx.browser.window.location.reload()
                }
            )
            return@AppPageLayout
        }

        val profileState by profileViewModel!!.uiState.collectAsState()

        // If the user reached /people by tapping a card in the Interactions
        // popup, the container is holding that PersonDto. Prepend them to the
        // queue so they're the top card, then clear the slot so a refresh
        // doesn't re-focus the same person forever.
        val pendingFocus by container.pendingFocusPerson.collectAsState()
        LaunchedEffect(pendingFocus) {
            val person = pendingFocus ?: return@LaunchedEffect
            peopleViewModel.focusPerson(person)
            container.setPendingFocusPerson(null)
        }

        PeopleContent(
            viewModel = peopleViewModel,
            profileViewModel = profileViewModel,
            profileState = profileState,
            sportName = sportName,
            mobile = mobile,
            cardRepository = container.cardRepository,
            onOpenProfile = { userId -> com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, userId) },
        )
    }
}

/**
 * Profile-completeness check — the swipe feed requires the same minimum
 * fields the backend filters discoverable people on, so the caller can
 * actually be considered "discoverable" themselves once they're ready.
 *
 * Requirements: full name, date of birth, city, at least one language,
 * at least one interest of any kind (sport / activity / lifestyle), and
 * at least one card avatar.
 */
/**
 * Build [PersonalInfoFormState] from the loaded user + ProfileViewModel
 * languages list. Mirrors the helper used on the Profile page so the
 * wizard's "Personal Info" step starts from the same baseline the
 * standalone editor does.
 */
private fun personalInfoInitialFromUser(
    user: UserDto,
    languages: List<String>,
): PersonalInfoFormState {
    val full = user.fullName.orEmpty().trim()
    val (first, last) = if (full.isEmpty()) "" to "" else {
        val parts = full.split(" ", limit = 2)
        parts[0] to parts.getOrNull(1).orEmpty()
    }
    return PersonalInfoFormState(
        firstName = first,
        lastName = last,
        dateOfBirth = isoDobToDisplay(user.dateOfBirth),
        gender = user.gender.orEmpty(),
        city = user.city.orEmpty(),
        // Seed the stored pick so saving an untouched form re-sends the same
        // coordinates instead of wiping them.
        cityLat = user.cityLat,
        cityLng = user.cityLng,
        languages = languages,
        bio = user.bio.orEmpty(),
    )
}

/** "YYYY-MM-DD" → "DD.MM.YYYY". Returns "" if not parseable. */
private fun isoDobToDisplay(iso: String?): String {
    if (iso.isNullOrBlank()) return ""
    val datePart = iso.substringBefore('T').trim()
    val parts = datePart.split('-')
    if (parts.size != 3) return ""
    val (y, m, d) = parts
    if (y.length != 4 || m.length !in 1..2 || d.length !in 1..2) return ""
    return "${d.padStart(2, '0')}.${m.padStart(2, '0')}.$y"
}

private fun UserDto.isReadyForPeople(): Boolean {
    val hasBasics =
        !fullName.isNullOrBlank() &&
            !city.isNullOrBlank() &&
            !dateOfBirth.isNullOrBlank()
    val hasLanguage = languages.isNotEmpty()
    val hasInterest =
        interestSports.isNotEmpty() ||
            interestActivities.isNotEmpty() ||
            interestLifestyle.isNotEmpty()
    val hasGeneratedAvatar = mainProfileCard?.avatarUrl?.isNotBlank() == true
    return hasBasics && hasLanguage && hasInterest && hasGeneratedAvatar
}

/**
 * Guest variant — renders the same Squad Up panel + cards backed by the
 * public people feed, but every action (swipe, info tap, like/skip button)
 * funnels through [onGated] which the parent uses to open the
 * AccountRequiredModal. No backend swipe/interaction calls fire.
 */
@Composable
private fun PeopleGuestContent(
    viewModel: PeopleViewModel,
    sportName: String,
    mobile: Boolean,
    onGated: () -> Unit,
) {
    val state = viewModel.uiState.collectAsState().value
    if (mobile) {
        MobilePeopleContent(
            state = state,
            sportName = sportName,
            gatedSwipes = true,
            onLike = onGated,
            onSkip = onGated,
            onSwipeAttempt = onGated,
            onInfo = onGated,
            onReload = { viewModel.loadMore() },
        )
        return
    }
    Column(
        modifier = Modifier
            .fillMaxSize()
            .sPadding(26, 40),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .sMaxWidth(928)
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px),
            contentAlignment = Alignment.TopCenter,
        ) {
            SquadUpPanel(
                state = state,
                sportName = sportName,
                gatedSwipes = true,
                onLike = onGated,
                onSkip = onGated,
                onSwipeAttempt = onGated,
                onInfo = onGated,
                onReload = { viewModel.loadMore() },
            )
        }
    }
}

@Composable
private fun PeopleContent(
    viewModel: PeopleViewModel,
    profileViewModel: ProfileViewModel,
    profileState: com.arena_web.core.viewmodel.ProfileUiState,
    sportName: String,
    mobile: Boolean,
    cardRepository: com.arena_web.domain.repositories.CardRepository?,
    onOpenProfile: (userId: String) -> Unit,
) {
    val state by viewModel.uiState.collectAsState()
    var detailFor by remember { mutableStateOf<PersonDto?>(null) }

    val currentUser = profileState.user
    val isProfileReady = currentUser?.isReadyForPeople() == true

    // Card templates are subscription-gated (mobile parity): with an active
    // sub the wizard's step 3 auto-claims every unowned template (no locks,
    // purchase modal only as claim-failure fallback); without one a locked
    // tile opens the mono paywall.
    val appContainer = LocalAppContainer.current
    val isSubscribed by appContainer.isSubscribed.collectAsState()
    LaunchedEffect(Unit) { appContainer.ensureSubscriptionLoaded() }
    var wizardPaywallOpen by remember { mutableStateOf(false) }
    LaunchedEffect(isSubscribed, profileState.ownedTemplates) {
        if (isSubscribed) profileViewModel.autoClaimTemplates()
    }
    com.arena_web.ui.components.overlay.SubscribeModal(
        isOpen = wizardPaywallOpen,
        onClose = { wizardPaywallOpen = false },
        source = "onboarding_card",
    )

    // Two-stage gate: tapping any action when the profile is incomplete
    // first opens [showPrompt]; "Complete profile" inside the prompt then
    // opens [showWizard]. Both are independent so the user can dismiss
    // either layer without losing the other's state.
    var showPrompt by remember { mutableStateOf(false) }
    var showWizard by remember { mutableStateOf(false) }

    fun guardOrRun(action: () -> Unit) {
        if (isProfileReady) action() else showPrompt = true
    }

    if (mobile) {
        MobilePeopleContent(
            state = state,
            sportName = sportName,
            gatedSwipes = !isProfileReady,
            onLike = { guardOrRun { viewModel.like() } },
            onSkip = { guardOrRun { viewModel.skip() } },
            onSwipeAttempt = { showPrompt = true },
            // Tapping a card (the info action) is also gated: an incomplete
            // profile gets the completion prompt instead of the detail view.
            onInfo = { guardOrRun { detailFor = viewModel.currentCard() } },
            onReload = { viewModel.loadMore() },
        )
    } else {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(26, 40),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(
                modifier = Modifier
                    .sMaxWidth(928)
                    .fillMaxWidth()
                    .flexGrow(1)
                    .minHeight(0.px),
                contentAlignment = Alignment.TopCenter,
            ) {
                SquadUpPanel(
                    state = state,
                    sportName = sportName,
                    gatedSwipes = !isProfileReady,
                    onLike = { guardOrRun { viewModel.like() } },
                    onSkip = { guardOrRun { viewModel.skip() } },
                    onSwipeAttempt = { showPrompt = true },
                    onInfo = { detailFor = viewModel.currentCard() },
                    onReload = { viewModel.loadMore() },
                )
            }
        }
    }

    CompleteProfilePromptDialog(
        isOpen = showPrompt,
        onCancel = { showPrompt = false },
        onCompleteProfile = {
            showPrompt = false
            showWizard = true
        },
    )

    if (showWizard && currentUser != null) {
        CompleteProfileWizardDialog(
            isOpen = true,
            initialPersonalInfo = personalInfoInitialFromUser(currentUser, profileState.languages),
            initialFocus = FocusActivitiesFormState(
                sports = profileState.interestSports,
                activities = profileState.interestActivities,
                lifestyle = profileState.interestLifestyle,
                notes = profileState.interestNotes,
            ),
            ownedTemplates = profileState.ownedTemplates,
            profileCards = profileState.profileCards,
            sportName = sportName,
            activeTemplateId = currentUser.mainProfileCard?.userCardId,
            hasGeneratedAvatar = currentUser.mainProfileCard?.avatarUrl?.isNotBlank() == true,
            cardRepository = cardRepository,
            pendingPurchase = profileState.pendingPurchase,
            purchasing = profileState.purchasing,
            purchaseError = profileState.purchaseError,
            onClose = { showWizard = false },
            onSavePersonalInfo = { form ->
                profileViewModel.updatePersonalInfo(
                    fullName = listOf(form.firstName, form.lastName)
                        .filter { it.isNotBlank() }
                        .joinToString(" "),
                    dateOfBirthDisplay = form.dateOfBirth,
                    city = form.city,
                    cityLat = form.cityLat,
                    cityLng = form.cityLng,
                    gender = form.gender,
                    bio = form.bio,
                    languages = form.languages,
                )
            },
            onSaveFocus = { form ->
                profileViewModel.updateInterests(
                    sports = form.sports,
                    activities = form.activities,
                    lifestyle = form.lifestyle,
                    notes = form.notes,
                )
            },
            onAvatarConfirmed = { profileViewModel.loadProfile() },
            onPurchaseRequest = { template ->
                if (isSubscribed) profileViewModel.requestPurchase(template) else wizardPaywallOpen = true
            },
            onPurchaseConfirm = { profileViewModel.confirmPurchase() },
            onPurchaseCancel = { profileViewModel.cancelPurchase() },
            onReorderCards = { ids -> profileViewModel.reorderProfileCards(ids) },
        )
    }

    PersonDetailDialog(
        person = detailFor,
        sportName = sportName,
        onDismiss = { detailFor = null },
    )

    // Same trigger + data; phones get the portrait full-screen variant
    // (Figma 8407:2791), desktop keeps the landscape popup.
    if (mobile) {
        com.arena_web.ui.components.overlay.MobileMatchPopup(
            matched = state.matchedPerson,
            currentUser = currentUser,
            currentUserCard = currentUser?.mainProfileCard,
            sportName = sportName,
            onClose = { viewModel.dismissMatch() },
            onGoToProfile = { userId ->
                viewModel.dismissMatch()
                onOpenProfile(userId)
            },
        )
    } else {
        MatchPopup(
            matched = state.matchedPerson,
            currentUser = currentUser,
            currentUserCard = currentUser?.mainProfileCard,
            sportName = sportName,
            onClose = { viewModel.dismissMatch() },
            onGoToProfile = { userId ->
                viewModel.dismissMatch()
                onOpenProfile(userId)
            },
        )
    }
}

@Composable
private fun SquadUpPanel(
    state: com.arena_web.core.viewmodel.PeopleUiState,
    sportName: String,
    gatedSwipes: Boolean,
    onLike: () -> Unit,
    onSkip: () -> Unit,
    onSwipeAttempt: () -> Unit,
    onInfo: () -> Unit,
    onReload: () -> Unit,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(40)
            .styleModifier {
                property("backdrop-filter", "blur(${s(11.165)})")
                property("-webkit-backdrop-filter", "blur(${s(11.165)})")
                property(
                    "background",
                    "linear-gradient(174.7deg, rgba(60, 121, 241, 0.135) 13.9%, rgba(5, 4, 12, 0.03) 69.5%)",
                )
            }
            .sPadding(22)
            .sGap(40),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Header — "Squad Up" + subtitle aligned left.
        Column(
            modifier = Modifier.fillMaxWidth().sGap(16),
            horizontalAlignment = Alignment.Start,
        ) {
            SpanText(
                t.pageSquadUpTitle,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(32)
                    .sLetterSpacing(0.24)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
            SpanText(
                t.pageSquadUpSubtitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
            )
        }

        val current = state.queue.firstOrNull()
        when {
            state.isLoading && current == null -> {
                LoadingState()
            }
            current == null -> {
                EmptyState(
                    hasMore = state.hasMore,
                    error = state.error,
                    onReload = onReload,
                )
            }
            else -> {
                // `pendingCommit` is the single source of truth for "this
                // card is about to slide off". Both the drag gesture (inside
                // PeopleCardStack) and the action buttons below set it; the
                // card animates accordingly, then `onAnimationDone` fires the
                // actual viewModel call and clears the state.
                //
                // Keyed by userId so a fresh card starts in a clean state
                // when the queue advances.
                var pendingCommit by remember(current.userId) {
                    mutableStateOf<String?>(null)
                }
                PeopleCardStack(
                    current = current,
                    sportName = sportName,
                    pendingCommit = pendingCommit,
                    // Always let the card play its fly-off animation — the
                    // gating decision happens in `onAnimationDone`. The card
                    // resets to centre behind the prompt overlay (z-index
                    // 1050), so the user never sees the snap-back.
                    onDragCommit = { dir -> pendingCommit = dir },
                    onAnimationDone = {
                        if (gatedSwipes) {
                            onSwipeAttempt()
                        } else {
                            when (pendingCommit) {
                                COMMIT_LIKE, COMMIT_LIKE_BUTTON -> onLike()
                                COMMIT_SKIP, COMMIT_SKIP_BUTTON -> onSkip()
                            }
                        }
                        pendingCommit = null
                    },
                )
                ActionRow(
                    enabled = !state.isSwiping && pendingCommit == null,
                    // Button-tap commits use the `_BUTTON` variants so the
                    // card animation is shorter and less rotated than a
                    // user-initiated drag.
                    onSkip = { pendingCommit = COMMIT_SKIP_BUTTON },
                    onInfo = onInfo,
                    onLike = { pendingCommit = COMMIT_LIKE_BUTTON },
                )
                if (state.error != null) {
                    SpanText(
                        state.error,
                        modifier = Modifier.sFontSize(14).color(AppColors.Error),
                    )
                }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Box(
        modifier = Modifier.fillMaxWidth().sHeight(500),
        contentAlignment = Alignment.Center,
    ) {
        // Intentionally empty — no loading indicator (user pref).
    }
}

@Composable
private fun EmptyState(hasMore: Boolean, error: String?, onReload: () -> Unit) {
    val t = LocalAppStrings.current
    // The "still paging" tier is intentionally rendered as a spinner instead
    // of a text card — it's an in-flight transition, not a terminal state,
    // so showing a hero copy block would mislead the user.
    if (hasMore && error == null) {
        Box(
            modifier = Modifier.fillMaxWidth().sHeight(500),
            contentAlignment = Alignment.Center,
        ) {
            // Intentionally empty — no loading indicator (user pref).
        }
        return
    }

    val (title, subtitle) = if (error != null) {
        t.pagePeopleLoadFailed to error
    } else {
        t.pagePeopleCaughtUpTitle to t.pagePeopleCaughtUpSubtitle
    }
    val sportColors = LocalSportColors.current
    val primaryRgb = sportColors.primary.toRgb().let { "rgb(${it.red}, ${it.green}, ${it.blue})" }
    Column(
        modifier = Modifier.fillMaxWidth().sHeight(500).sGap(28),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(modifier = Modifier.flexGrow(1))
        Icon(src = AppIcons.EmptyMatches)
        Column(
            modifier = Modifier.sGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .sFontSize(26)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(30)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") },
            )
            Box(modifier = Modifier.sMaxWidth(420)) {
                SpanText(
                    subtitle,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(22)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("text-align", "center") },
                )
            }
        }
        // Primary-blue CTA — mirrors the "Log in" pill in PeoplePlaceholder so
        // the two error-recovery paths feel the same.
        Box(
            modifier = Modifier
                .sHeight(46)
                .sBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onReload() }
                .styleModifier {
                    property("padding", "${s(14)} ${s(32)}")
                    property("background", primaryRgb)
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                t.pageRefresh,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(sportColors.onPrimary),
            )
        }
        Box(modifier = Modifier.flexGrow(1))
    }
}

@Composable
private fun ActionRow(
    enabled: Boolean,
    onSkip: () -> Unit,
    onInfo: () -> Unit,
    onLike: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().sGap(27),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Per Figma 7502:299510 — all three buttons share the same dark
        // translucent navy disc; only the centered glyph colour changes.
        CircleActionButton(
            iconPath = AppIcons.Close,
            iconColor = ACTION_RED,
            enabled = enabled,
            onClick = onSkip,
        )
        CircleActionButton(
            iconPath = AppIcons.Information,
            iconColor = ACTION_INFO,
            // "i" glyph is tall/narrow — shrink it so the visual weight
            // matches the X/check icons on either side.
            iconSize = 22,
            enabled = enabled,
            onClick = onInfo,
        )
        CircleActionButton(
            iconPath = AppIcons.Check,
            iconColor = ACTION_GREEN,
            enabled = enabled,
            onClick = onLike,
        )
    }
}

private const val ACTION_RED = "#FF4242"
private const val ACTION_GREEN = "#7BFA6F"
private const val ACTION_INFO = "#BED6F5"
private const val ACTION_DISC_BG = "rgba(11, 22, 46, 0.85)"

@Composable
private fun CircleActionButton(
    iconPath: String,
    iconColor: String,
    enabled: Boolean,
    onClick: () -> Unit,
    iconSize: Int = 36,
) {
    Box(
        modifier = Modifier
            .sSize(72)
            .sBorderRadius(150)
            .then(if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("position", "relative")
                property("background", ACTION_DISC_BG)
                property("backdrop-filter", "blur(4px)")
                if (!enabled) property("opacity", "0.5")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Shared design-system gradient ring (mask-composite via
        // `ButtonBorderGradientPrimary`) — same outline every outlined pill
        // in the app uses.
        GradientBorderRing(radiusPx = 150)
        // CSS-mask recolours the monochrome SVG to the action's tint without
        // needing per-button SVG assets. The icon's shape comes from the
        // mask; its fill is `background-color`.
        Box(
            modifier = Modifier
                .sSize(iconSize)
                .styleModifier {
                    property("background-color", iconColor)
                    property("mask", "url('$iconPath') no-repeat center / contain")
                    property("-webkit-mask", "url('$iconPath') no-repeat center / contain")
                },
        )
    }
}

/**
 * Friendly empty/lock state — used for both unauthenticated and
 * profile-incomplete cases. Lives inside the same dark panel as the
 * card stack so the page chrome doesn't jump.
 */
@Composable
private fun PeoplePlaceholder(
    title: String,
    message: String,
    ctaLabel: String,
    onCta: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .sPadding(26, 40),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(700)
                .fillMaxWidth()
                .flexGrow(1)
                .sBorderRadius(40)
                .sPadding(40)
                .sGap(20)
                .styleModifier {
                    property("backdrop-filter", "blur(${s(11.165)})")
                    property("-webkit-backdrop-filter", "blur(${s(11.165)})")
                    property(
                        "background",
                        "linear-gradient(174.7deg, rgba(60, 121, 241, 0.135) 13.9%, rgba(5, 4, 12, 0.03) 69.5%)",
                    )
                },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Box(modifier = Modifier.flexGrow(1))
            Box(
                modifier = Modifier
                    .sSize(96)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("background", AppGradients.InteractiveSecondary)
                        property("border", "0.3px solid var(--arena-border-top)")
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Globe, modifier = Modifier.sSize(48))
            }
            SpanText(
                title,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(32)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") },
            )
            SpanText(
                message,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                    .styleModifier {
                        property("text-align", "center")
                        property("max-width", s(480))
                    },
            )
            val sportColors = LocalSportColors.current
            val primaryRgb = sportColors.primary.toRgb().let { "rgb(${it.red}, ${it.green}, ${it.blue})" }
            Box(
                modifier = Modifier
                    .sHeight(46)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { onCta() }
                    .styleModifier {
                        property("padding", "${s(14)} ${s(32)}")
                        property("background", primaryRgb)
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    ctaLabel,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(LocalSportColors.current.onPrimary),
                )
            }
            Box(modifier = Modifier.flexGrow(1))
        }
    }
}
